import React, { useEffect, useState, useCallback } from 'react';
import { patientHistoryService, type FamilyHealthHistory } from '../services/patientHistoryService';
import EmptyState from '../../../components/ui/EmptyState';
import ConfirmModal from '../../../components/common/ConfirmModal';
import Spinner from '../../../components/ui/Spinner';

interface FamilyHistoryProps {
  patientId: number;
}

interface FormData {
  relative_type: 'father' | 'mother' | 'sibling' | 'grandparent' | 'child' | 'other' | '';
  condition_name: string;
  icd10_code: string;
  age_at_diagnosis: number | '';
  severity: 'mild' | 'moderate' | 'severe' | '';
  notes: string;
}

const emptyForm: FormData = {
  relative_type: '',
  condition_name: '',
  icd10_code: '',
  age_at_diagnosis: '',
  severity: '',
  notes: '',
};

const RELATIVE_LABELS: Record<string, string> = {
  father: 'Padre',
  mother: 'Madre',
  sibling: 'Hermano/Hermana',
  grandparent: 'Abuelo/Abuela',
  child: 'Hijo/Hija',
  other: 'Otro',
};

const SEVERITY_LABELS: Record<string, string> = {
  mild: 'Leve',
  moderate: 'Moderado',
  severe: 'Severo',
};

const FamilyHistory: React.FC<FamilyHistoryProps> = ({ patientId }) => {
  const [items, setItems] = useState<FamilyHealthHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formData, setFormData] = useState<FormData>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const data = await patientHistoryService.getFamilyHistory(patientId);
      setItems(data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al cargar antecedentes familiares');
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    void fetchItems();
  }, [fetchItems]);

  const handleChange = (field: keyof FormData, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.condition_name.trim()) {
      setError('El nombre de la condición es requerido');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const payload = {
        relative_type: formData.relative_type || null,
        condition_name: formData.condition_name.trim(),
        icd10_code: formData.icd10_code.trim() || null,
        age_at_diagnosis: formData.age_at_diagnosis === '' ? null : Number(formData.age_at_diagnosis),
        severity: formData.severity || null,
        notes: formData.notes.trim() || null,
      };

      if (editingId !== null) {
        await patientHistoryService.updateFamilyHistory(patientId, editingId, payload);
      } else {
        await patientHistoryService.createFamilyHistory(patientId, payload);
      }

      setFormData(emptyForm);
      setShowForm(false);
      setEditingId(null);
      void fetchItems();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al guardar antecedente');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (item: FamilyHealthHistory) => {
    setEditingId(item.id!);
    setFormData({
      relative_type: item.relative_type ?? '',
      condition_name: item.condition_name,
      icd10_code: item.icd10_code ?? '',
      age_at_diagnosis: item.age_at_diagnosis ?? '',
      severity: item.severity ?? '',
      notes: item.notes ?? '',
    });
    setShowForm(true);
  };

  const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);

  const requestDelete = (historyId: number) => setPendingDeleteId(historyId);

  const cancelDelete = () => {
    if (deleting) return;
    setPendingDeleteId(null);
  };

  const confirmDelete = async () => {
    if (pendingDeleteId == null) return;
    setDeleting(true);
    const id = pendingDeleteId;
    setPendingDeleteId(null);
    try {
      await patientHistoryService.deleteFamilyHistory(patientId, id);
      void fetchItems();
    } catch (err) {
      console.error('Error deleting:', err);
    } finally {
      setDeleting(false);
    }
  };

  const handleCancel = () => {
    setFormData(emptyForm);
    setShowForm(false);
    setEditingId(null);
    setError(null);
  };

  if (loading) {
    return <Spinner variant="full-page" text="Cargando antecedentes familiares..." />;
  }

  return (
    <div className="card">
      <div className="card-header">
        <h3 className="card-title">
          <i className="fa fa-users"></i> Antecedentes Familiares
        </h3>
        <div className="card-tools">
          <button
            className="btn btn-primary btn-sm"
            onClick={() => {
              setShowForm(!showForm);
              setEditingId(null);
              setFormData(emptyForm);
              setError(null);
            }}
          >
            <i className={`fa ${showForm ? 'fa-times' : 'fa-plus'}`}></i>
            {showForm ? ' Cancelar' : ' Agregar'}
          </button>
        </div>
      </div>
      <div className="card-body">
        {error && (
          <div className="alert alert-danger alert-sm mb-3">
            <i className="fa fa-exclamation-circle me-2"></i>
            {error}
          </div>
        )}

        {/* Form */}
        {showForm && (
          <div className="card card-info mb-4" style={{ backgroundColor: '#f9f9f9' }}>
            <div className="card-header">
              <h4 className="card-title">
                {editingId !== null ? 'Editar Antecedente' : 'Nuevo Antecedente'}
              </h4>
            </div>
            <div className="card-body">
              <form onSubmit={handleSubmit}>
                <div className="row">
                  {/* Relative Type */}
                  <div className="col-md-4">
                    <div className="form-group">
                      <label>Parentesco</label>
                      <select
                        className="form-control"
                        value={formData.relative_type}
                        onChange={(e) => handleChange('relative_type', e.target.value)}
                      >
                        <option value="">Seleccionar...</option>
                        <option value="father">Padre</option>
                        <option value="mother">Madre</option>
                        <option value="sibling">Hermano/Hermana</option>
                        <option value="grandparent">Abuelo/Abuela</option>
                        <option value="child">Hijo/Hija</option>
                        <option value="other">Otro</option>
                      </select>
                    </div>
                  </div>

                  {/* Condition Name */}
                  <div className="col-md-4">
                    <div className="form-group">
                      <label>Nombre de la Condición *</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.condition_name}
                        onChange={(e) => handleChange('condition_name', e.target.value)}
                        placeholder="Ej: Hipertensión Arterial"
                        required
                      />
                    </div>
                  </div>

                  {/* ICD-10 Code */}
                  <div className="col-md-4">
                    <div className="form-group">
                      <label>Código ICD-10</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.icd10_code}
                        onChange={(e) => handleChange('icd10_code', e.target.value)}
                        placeholder="Ej: I10"
                      />
                    </div>
                  </div>
                </div>

                <div className="row">
                  {/* Age at Diagnosis */}
                  <div className="col-md-4">
                    <div className="form-group">
                      <label>Edad al Diagnóstico</label>
                      <input
                        type="number"
                        className="form-control"
                        value={formData.age_at_diagnosis}
                        onChange={(e) => handleChange('age_at_diagnosis', e.target.value)}
                        min={0}
                        max={120}
                        placeholder="Ej: 55"
                      />
                    </div>
                  </div>

                  {/* Severity */}
                  <div className="col-md-4">
                    <div className="form-group">
                      <label>Severidad</label>
                      <select
                        className="form-control"
                        value={formData.severity}
                        onChange={(e) => handleChange('severity', e.target.value)}
                      >
                        <option value="">Seleccionar...</option>
                        <option value="mild">Leve</option>
                        <option value="moderate">Moderado</option>
                        <option value="severe">Severo</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Notes */}
                <div className="row">
                  <div className="col-md-12">
                    <div className="form-group">
                      <label>Notas</label>
                      <textarea
                        className="form-control"
                        rows={2}
                        value={formData.notes}
                        onChange={(e) => handleChange('notes', e.target.value)}
                        placeholder="Observaciones adicionales..."
                      />
                    </div>
                  </div>
                </div>

                <button type="submit" className="btn btn-primary btn-sm" disabled={saving}>
                  {saving ? (
                    <Spinner size="sm" text="Guardando..." />
                  ) : (
                    <><i className="fa fa-save"></i> {editingId !== null ? 'Actualizar' : 'Guardar'}</>
                  )}
                </button>
                <button type="button" className="btn btn-outline-secondary btn-sm ms-2" onClick={handleCancel}>
                  Cancelar
                </button>
              </form>
            </div>
          </div>
        )}

        {/* List */}
        {items.length === 0 && !showForm ? (
          <EmptyState
            icon="fa-users"
            title="No tiene antecedentes familiares registrados"
            className="py-4"
          />
        ) : (
          <div className="table-responsive">
            <table className="table table-striped table-hover">
              <thead>
                <tr>
                  <th>Parentesco</th>
                  <th>Condición</th>
                  <th>Código ICD-10</th>
                  <th>Edad al Dx</th>
                  <th>Severidad</th>
                  <th>Notas</th>
                  <th style={{ width: '100px' }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id}>
                    <td>
                      {item.relative_type && (
                        <span className="badge bg-info">
                          {RELATIVE_LABELS[item.relative_type]}
                        </span>
                      )}
                    </td>
                    <td><strong>{item.condition_name}</strong></td>
                    <td><code>{item.icd10_code || '-'}</code></td>
                    <td>{item.age_at_diagnosis ?? '-'}</td>
                    <td>
                      {item.severity && (
                        <span className={`badge ${
                          item.severity === 'mild' ? 'bg-success' :
                          item.severity === 'moderate' ? 'bg-warning' :
                          'bg-danger'
                        }`}>
                          {SEVERITY_LABELS[item.severity]}
                        </span>
                      )}
                    </td>
                    <td><small className="text-muted">{item.notes || '-'}</small></td>
                    <td>
                      <button
                        className="btn btn-xs btn-primary me-1"
                        title="Editar"
                        onClick={() => handleEdit(item)}
                      >
                        <i className="fa fa-edit"></i>
                      </button>
                      <button
                        className="btn btn-xs btn-danger"
                        title="Eliminar"
                        onClick={() => requestDelete(item.id!)}
                      >
                        <i className="fa fa-trash"></i>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ConfirmModal
        show={pendingDeleteId !== null}
        title="Eliminar antecedente familiar"
        body="¿Está seguro que desea eliminar este antecedente familiar? Esta acción no se puede deshacer."
        confirmLabel="Eliminar"
        cancelLabel="Cancelar"
        confirmVariant="danger"
        loading={deleting}
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
      />
    </div>
  );
};

export default FamilyHistory;
